import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Plus, Trash2, Save } from 'lucide-react'
import { offerten as offertenApi, kunden as kundenApi } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { formatWaehrung, berechneTotalOfferte } from '../../lib/utils'
import type { Offerte, OffertPosition } from '../../types'

type NewPosition = Omit<OffertPosition, 'id' | 'offerte_id' | 'sort_order'>

const GERAETE_PREISLISTE = [
  { label: 'DS2 Home', preis: 3190 },
  { label: 'DS2 Pro', preis: 3690 },
  { label: 'DS2 Pro mit Cart', preis: 4190 },
  { label: 'DS4', preis: 6290 },
  { label: 'DS4+ mit Vakuum-Kit', preis: 6990 },
  { label: 'DS4+ Mobility mit Vakuum-Kit', preis: 6990 },
  { label: 'Abo DS2 Pro (pro Monat)', preis: 210 },
  { label: 'Abo DS4 (pro Monat)', preis: 290 },
  { label: 'Servicebesuch', preis: 180 },
  { label: 'Installation und Einweisung', preis: 350 },
]

export function OffertFormPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const [kundeId, setKundeId] = useState('')
  const [gueltigBis, setGueltigBis] = useState('')
  const [notizen, setNotizen] = useState('')
  const [positionen, setPositionen] = useState<NewPosition[]>([
    { beschreibung: '', menge: 1, einheit: 'Stück', einzelpreis: 0, rabatt: 0 }
  ])

  const { data: kundenList } = useQuery({ queryKey: ['kunden'], queryFn: () => kundenApi.list() })

  const mutation = useMutation({
    mutationFn: (data: Partial<Offerte> & { positionen: NewPosition[] }) => offertenApi.create(data),
    onSuccess: (o) => { qc.invalidateQueries({ queryKey: ['offerten'] }); navigate(`/offerten/${o.id}`) },
  })

  const addPosition = () => setPositionen([...positionen, { beschreibung: '', menge: 1, einheit: 'Stück', einzelpreis: 0, rabatt: 0 }])
  const removePosition = (i: number) => setPositionen(positionen.filter((_, idx) => idx !== i))
  const updatePosition = (i: number, k: string, v: string | number) =>
    setPositionen(positionen.map((p, idx) => idx === i ? { ...p, [k]: v } : p))

  const addVorlage = (vorlage: typeof GERAETE_PREISLISTE[0]) => {
    setPositionen([...positionen, { beschreibung: vorlage.label, menge: 1, einheit: 'Stück', einzelpreis: vorlage.preis, rabatt: 0 }])
  }

  const total = berechneTotalOfferte(positionen)

  return (
    <>
      <TopBar title="Neue Offerte" backTo="/offerten" />
      <div className="px-4 py-3 pb-8 space-y-4">
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Grundinformationen</h3>
          <div className="space-y-4">
            <Select
              label="Kunde" required
              value={kundeId} onChange={(e) => setKundeId(e.target.value)}
              options={(kundenList ?? []).map((k) => ({ value: k.id, label: k.firma ?? k.name }))}
              placeholder="Kunde auswählen"
            />
            <Input label="Gültig bis" type="date" value={gueltigBis} onChange={(e) => setGueltigBis(e.target.value)} />
            <Textarea label="Notizen" placeholder="Interne Notizen..." rows={2} value={notizen} onChange={(e) => setNotizen(e.target.value)} />
          </div>
        </Card>

        {/* Vorlagen */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-3">Produkte hinzufügen</h3>
          <div className="flex flex-wrap gap-2">
            {GERAETE_PREISLISTE.map((v) => (
              <button key={v.label} onClick={() => addVorlage(v)}
                className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-full hover:bg-blue-100 transition-colors">
                {v.label} ({formatWaehrung(v.preis)})
              </button>
            ))}
          </div>
        </Card>

        {/* Positionen */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Positionen</h3>
            <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={addPosition}>Hinzufügen</Button>
          </div>
          <div className="space-y-4">
            {positionen.map((p, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Position {i + 1}</span>
                  {positionen.length > 1 && (
                    <button onClick={() => removePosition(i)} className="text-red-500 p-1">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <Input label="Beschreibung" value={p.beschreibung} onChange={(e) => updatePosition(i, 'beschreibung', e.target.value)} placeholder="Produkt oder Dienstleistung" required />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Menge" type="number" value={p.menge} min={0.1} step={0.5} onChange={(e) => updatePosition(i, 'menge', Number(e.target.value))} />
                  <Select label="Einheit" value={p.einheit} onChange={(e) => updatePosition(i, 'einheit', e.target.value)}
                    options={[{ value: 'Stück', label: 'Stück' }, { value: 'Monat', label: 'Monat' }, { value: 'Std.', label: 'Stunden' }, { value: 'Pausch.', label: 'Pauschale' }]} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Einzelpreis (CHF)" type="number" value={p.einzelpreis} step={0.05} onChange={(e) => updatePosition(i, 'einzelpreis', Number(e.target.value))} />
                  <Input label="Rabatt (%)" type="number" value={p.rabatt} min={0} max={100} onChange={(e) => updatePosition(i, 'rabatt', Number(e.target.value))} />
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold text-gray-800">
                    {formatWaehrung(p.menge * p.einzelpreis * (1 - p.rabatt / 100))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Total */}
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-blue-900">Total (exkl. MwSt.)</span>
            <span className="text-2xl font-bold text-blue-900">{formatWaehrung(total)}</span>
          </div>
        </Card>

        {mutation.error && <p className="text-sm text-red-600 text-center">{mutation.error.message}</p>}

        <Button
          type="button" fullWidth size="lg" icon={<Save size={18} />} loading={mutation.isPending}
          disabled={!kundeId || positionen.some((p) => !p.beschreibung)}
          onClick={() => mutation.mutate({ kunde_id: kundeId, ersteller: user?.name ?? '', gueltig_bis: gueltigBis, notizen, positionen: positionen as unknown as OffertPosition[] })}
        >
          Offerte erstellen
        </Button>
      </div>
    </>
  )
}
